/**
 * Input log: the full record of a run (spec 4.3). A run is
 * {levelId/seed, tuningVersion, inputLog} — re-simulating must reproduce the
 * identical outcome. Entries are state snapshots at the tick they changed;
 * replay expands them back into per-tick frames.
 */
import { TUNING_VERSION } from "../config/tuning";
import type { InputFrame } from "./sim";

export interface InputLogEntry {
  tick: number;
  steer: number;
  jumpHeld: boolean;
  accel: boolean;
  brake: boolean;
}

export interface RunRecord {
  levelId: string;
  tuningVersion: number;
  entries: InputLogEntry[];
}

export class InputLog {
  readonly entries: InputLogEntry[] = [];
  private lastSteer = 0;
  private lastJump = false;
  private lastAccel = false;
  private lastBrake = false;

  /** Record the frame consumed at `tick` (only stores changes). */
  record(tick: number, frame: InputFrame): void {
    if (
      this.entries.length === 0 ||
      frame.steer !== this.lastSteer ||
      frame.jumpHeld !== this.lastJump ||
      frame.accel !== this.lastAccel ||
      frame.brake !== this.lastBrake
    ) {
      this.entries.push({
        tick,
        steer: frame.steer,
        jumpHeld: frame.jumpHeld,
        accel: frame.accel,
        brake: frame.brake,
      });
      this.lastSteer = frame.steer;
      this.lastJump = frame.jumpHeld;
      this.lastAccel = frame.accel;
      this.lastBrake = frame.brake;
    }
  }

  toRecord(levelId: string): RunRecord {
    return { levelId, tuningVersion: TUNING_VERSION, entries: [...this.entries] };
  }
}

/** Replays a RunRecord as per-tick frames. Ticks are 1-based (first step = 1). */
export class InputLogPlayer {
  private idx = 0;
  private current: InputFrame = { steer: 0, jumpHeld: false, accel: false, brake: false };

  constructor(private readonly record: RunRecord) {}

  frameAt(tick: number): InputFrame {
    while (this.idx < this.record.entries.length && this.record.entries[this.idx]!.tick <= tick) {
      const e = this.record.entries[this.idx]!;
      this.current = {
        steer: e.steer,
        jumpHeld: e.jumpHeld,
        accel: e.accel ?? false,
        brake: e.brake ?? false,
      };
      this.idx++;
    }
    return this.current;
  }
}
